import React from 'react';
import { toast } from 'react-toastify';
import ChangeRequestList from '../../../Components/crude/ChangeRequestList';
import useChangeRequests from '../../../Components/crude/useChangeRequests';
import { CHANGE_REQUEST_ENDPOINTS } from '../../../Components/crude/changeRequestConstants';
import { crudeApi } from '../../../helpers/crude_api_helper';

// Reviewal List — first stage of the maker-checker workflow.
// Server filter is fixed to Status == AwaitingReview, so every row here is
// pending review. Approve -> Review (status becomes AwaitingApprove, i.e. it
// moves to the Approval List). Reject -> RejectReviewal (change is reverted in
// the feeder module and status becomes Rejected).
//
// NOTE: the API does not enforce the Crude_Reviewer permission on these
// endpoints — the gate must be applied on the FE (see summary/contract).
const ReviewalListView = () => {
  const {
    items,
    totalRows,
    loading,
    perPage,
    searchInput,
    setSearchInput,
    setPage,
    setPerPage,
    handleSort,
    refetch,
  } = useChangeRequests({
    endpoint: CHANGE_REQUEST_ENDPOINTS.REVIEWAL_LIST,
    detailKey: 'detailReviewals',
  });

  // Action endpoints are [FromForm]. A state-machine violation (row already
  // moved to another status, e.g. a race with another reviewer) comes back as
  // 422 with the real reason in `detail` (surfaced by the api helper) — safe
  // to show verbatim. A genuine server fault (500) shows a generic message.
  const runAction = async (endpoint, row, notes, successMessage) => {
    try {
      await crudeApi.postForm(endpoint, { requestId: row.id, notes });
      toast.success(successMessage);
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      refetch();
    }
  };

  const renderActions = (row, { confirm }) => (
    <div className='d-flex gap-1 justify-content-center'>
      <button
        type='button'
        className='btn btn-soft-success btn-sm'
        onClick={() =>
          confirm({
            title: 'Approve Change Request',
            message: `Approve change on "${row.dataKey}"? It will be moved to the Approval List.`,
            confirmLabel: 'Approve',
            confirmColor: 'success',
            showNotes: true,
            onConfirm: notes =>
              runAction(
                CHANGE_REQUEST_ENDPOINTS.REVIEW,
                row,
                notes,
                'Change request approved and moved to Approval List'
              ),
          })
        }
      >
        Approve
      </button>
      <button
        type='button'
        className='btn btn-soft-danger btn-sm'
        onClick={() =>
          confirm({
            title: 'Reject Change Request',
            message: `Reject change on "${row.dataKey}"? This will revert it in the feeder module.`,
            confirmLabel: 'Reject',
            confirmColor: 'danger',
            showNotes: true,
            onConfirm: notes =>
              runAction(
                CHANGE_REQUEST_ENDPOINTS.REJECT_REVIEWAL,
                row,
                notes,
                'Change request rejected'
              ),
          })
        }
      >
        Reject
      </button>
    </div>
  );

  return (
    <ChangeRequestList
      title='Reviewal List'
      cardTitle='Reviewal List'
      documentTitle='Reviewal List'
      data={items}
      totalRows={totalRows}
      loading={loading}
      perPage={perPage}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      renderActions={renderActions}
      emptyText='No data to review'
    />
  );
};

export default ReviewalListView;
